import {AxiosError} from 'axios';
import {Trash2} from 'lucide';
import React from 'react';
import {Breadcrumb} from '~/components/Breadcrumps';
import {Button} from '~/components/Button';
import {useConfirm} from '~/components/Dialogs';
import {LucideIcon} from '~/components/LucideIcon';
import {useModals} from '~/components/Modal';
import {Progress} from '~/components/Progress';
import {BreadcrumbsResourcePage} from '~/components/ResourcePage';
import {TableRowActions, TableSort, TableSortDirection} from '~/components/Table';
import {Intent} from '~/lib/intent';
import {sortByString} from '~/lib/sort';
import {ICreatedApiToken} from '~/modules/Profile/models/ApiToken';
import {CreateTokenForm} from '~/modules/Profile/forms/CreateTokenForm';
import {ShowTokenModal} from '~/modules/Profile/forms/ShowTokenModal';
import {useApiTokensQuery, useCreateApiTokenMutation, useDeleteApiTokenMutation} from '~/modules/Profile/queries';
import {formatDateTime} from '~/lib/date';

export const ApiTokensView = () => {
    const {data: tokens = [], isPending} = useApiTokensQuery();
    const createMutation = useCreateApiTokenMutation();
    const deleteMutation = useDeleteApiTokenMutation();

    const modals = useModals();
    const confirm = useConfirm();

    const showTokenModal = modals.create(ShowTokenModal, {} as {token: ICreatedApiToken});

    const createTokenModal = modals.create(CreateTokenForm, {
        onSubmit: (values, formikActions, close) => {
            createMutation.mutate(values.name, {
                onSuccess: (created) => {
                    close();
                    showTokenModal.show({token: created});
                },
                onError: (error: AxiosError<{error?: string}>) => {
                    formikActions.setSubmitting(false);
                    const code = error.response?.data?.error;
                    if (code === 'name_required') {
                        formikActions.setFieldError('name', 'is required');
                    } else if (code === 'name_already_exists') {
                        formikActions.setFieldError('name', 'already exists');
                    } else {
                        formikActions.setStatus(error.message);
                    }
                },
            });
        },
    });

    return (
        <Breadcrumb breadcrumb={{title: 'API Tokens'}}>
            <BreadcrumbsResourcePage
                title="API Tokens"
                toolbar={
                    <>
                        <Button
                            intent={Intent.PRIMARY}
                            responsive
                            onClick={() => createTokenModal.show()}
                            leftIcon={<i className="fa fa-plus"/>}
                        >
                            Generate token
                        </Button>
                    </>
                }
            >
                <div className="layout-content-box">
                    <TableSort
                        records={tokens}
                        defaultSort={{
                            sort: sortByString,
                            field: 'name',
                            direction: TableSortDirection.ASC,
                        }}
                        header
                        columns={[
                            {
                                header: 'Name',
                                field: 'name',
                                sort: sortByString,
                            },
                            {
                                header: 'Last used',
                                field: 'lastUsed',
                                responsive: true,
                                cell: (t) => formatDateTime(t.lastUsed),
                            },
                            {
                                header: 'Expires',
                                field: 'expiration',
                                responsive: true,
                                cell: (t) => formatDateTime(t.expiration),
                            },
                            {
                                small: true,
                                cell: (t) => (
                                    <TableRowActions>
                                        <Button
                                            small
                                            variant="menu"
                                            leftIcon={<LucideIcon icon={Trash2} size={14}/>}
                                            onClick={() =>
                                                confirm('Revoke token?', {
                                                    onOk: () => deleteMutation.mutate(t.id),
                                                    icon: <LucideIcon icon={Trash2} size={20}/>,
                                                    intent: Intent.DANGER,
                                                })
                                            }
                                        >
                                            Revoke
                                        </Button>
                                    </TableRowActions>
                                ),
                            },
                        ]}
                        placeholder={isPending && <Progress/>}
                    />
                </div>
            </BreadcrumbsResourcePage>
        </Breadcrumb>
    );
};
